import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {CockpitCard} from 'shared/dtos/cockpit-card';
import {CockpitKontrolleM1} from 'shared/dtos/cockpit-kontrolle/cockpit-kontrolle';
import {FunctionRating} from 'shared/dtos/function-rating';
import {DataStorageService} from 'shared/services/data-storage.service';
import {HtmlTableParserService} from 'shared/services/table-data-export/html-table-parser.service';
import {TableExportService} from 'shared/services/table-data-export/table-data-export.service';
import {
  Table,
  TableCellProps
} from 'shared/services/table-data-export/table-properties';
import {
  CockpitChartComponent,
  CockpitContentComponent
} from 'src/app/cockpit/cockpit-shared/cockpit-content-component';
import {CurrentCodesTableComponent} from './current-codes-table/current-codes-table.component';
import {Funktionsmatrix} from './funktionsmatrix.interface';

@Component({
  selector: 'app-chart-current-codes',
  templateUrl: './chart-current-codes.component.html',
  styleUrl: './chart-current-codes.component.scss',
  standalone: false
})
export class ChartCurrentCodesComponent
  implements OnInit, CockpitContentComponent
{
  public data: Funktionsmatrix;
  public funktionen: FunctionRating[];

  @ViewChild(CurrentCodesTableComponent, {read: ElementRef})
  currentCodesTableComponent!: ElementRef;

  chartChildren: CockpitChartComponent[];
  card: CockpitCard;

  constructor(
    private readonly storage: DataStorageService,
    private readonly tableExport: TableExportService,
    private readonly tableParser: HtmlTableParserService,
    private readonly translateService: TranslateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.data = (
      (await this.storage.getCockpitKontrolle()) as CockpitKontrolleM1
    ).funktionsmatrix;
    this.funktionen = await this.storage.getFunktionen();
  }

  prepareTables(tables: Table[]) {
    for (const table of tables) {
      const m = this.translateService.instant('i18n.common.m');
      const f = this.translateService.instant('i18n.common.f');

      for (const row of table) {
        for (const cell of row) {
          const array = cell.value
            .toString()
            .replace(/ {2}/g, '')
            .split('\u00a0');

          cell.value = {
            richText: array.map((v, i, arr) =>
              this.createRichText(v, m, f, i, arr)
            )
          } as any;
        }
      }

      this.sanitizeLinebreaks(table);
    }

    return tables;
  }

  exportCurrentCodes() {
    const tables = this.tableParser.parseAllTables(
      this.currentCodesTableComponent.nativeElement
    );

    const matrix = this.prepareTables(tables)[0];

    this.tableExport.exportMatrix(
      matrix,
      'm1.cockpit-kontrolle.card.currentCodes.content.1',
      false,
      [],
      [
        {width: 30},
        {width: 3},
        {width: 40},
        {width: 40},
        {width: 40},
        {width: 40},
        {width: 40},
        {width: 40},
        {width: 40},
        {width: 40}
      ]
    );
  }

  private createRichText(text: string, m: string, f: string, i: number,  arr: string[] ) {    
    const onlyWomen = this.checkStrContainsSearchValue(text, '0f', 0);
    const onlyMen = this.checkStrContainsSearchValue(text, '0m', 1);
   
    text += i + 1 === arr.length ? '' : '\n';
    const italic =
      arr.length > 2 && (i + 1 === arr.length || i + 2 === arr.length);
    let argb = '00000000';
    if (onlyWomen) {
      argb = '0000c878';
    } else if (onlyMen) {
      argb = '0066afe9';
    }

    return {
      text,
      font: {
        italic,
        color: {
          argb,
          theme: 1
        }
      }
    };
  }

  private sanitizeLinebreaks(matrix: TableCellProps[][]): TableCellProps[][] {
    const newLines = [];
    for (const [i, row] of matrix.entries()) {
      let rowLines = 1;
      for (const [j, cell] of matrix[i].entries()) {
        const cellLines = (cell.value as any).richText.length;
        if (cellLines > rowLines) {
          rowLines = cellLines;
        }
      }

      for (const [j, cell] of matrix[i].entries()) {
        if (j > 0) {
          cell.rowspan = rowLines;
          cell.colspan = 1;
        }
      }
      newLines.push([i, rowLines - 1]);
    }

    let labelLines = 0;
    const emptyLine = [
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''},
      {value: ''}
    ];
    for (const lines of newLines.reverse()) {
      for (let i = 0; i < lines[1]; i++) {
        matrix.splice(parseInt(lines[0], 10) + 1, 0, emptyLine);
      }
      labelLines += lines[1];
    }

    matrix[2][0].rowspan += labelLines;
    matrix[0][2].colspan = 8;
    return matrix;
  }

  private checkStrContainsSearchValue(input: string, searchValue: string, index: number): boolean {
    const match = /\((?<parts>[^)]+)\)/.exec(input);
    const matchParts = match?.groups?.parts;
    if (!matchParts) {
      return false;
    }

    const parts = matchParts.split(',').map(item => item.trim());
    const part = parts[index];
    if (part === undefined || part === null) {
      return false;
    }

    return String(part).toLowerCase() === searchValue.toLowerCase();
  }
}
